import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none";

const GoogleG = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 2.04 6.87l3.66 2.84C6.57 7.04 9.02 5.04 12 5.04z" />
    <path fill="#4285F4" d="M23.25 12.26c0-.92-.08-1.6-.26-2.31H12v4.19h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.55 2.75c2.14-1.97 3.65-4.87 3.65-8.42z" />
    <path fill="#FBBC05" d="M5.71 14.29a7.18 7.18 0 0 1 0-4.58L2.04 6.87a11.27 11.27 0 0 0 0 10.26l3.67-2.84z" />
    <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.73l-3.55-2.75c-.95.65-2.23 1.11-3.91 1.11-2.98 0-5.43-2-6.3-4.76l-3.66 2.84c1.8 3.6 5.58 6.29 9.96 6.29z" />
  </svg>
);

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/members";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back to the order." : "Account created. Welcome to the order.");
      navigate("/members");
    } else {
      setError(res.error);
    }
  };

  return (
    <main className="relative overflow-hidden pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="login-page">
      <div className="w-full max-w-md border border-gold/25 bg-card2/60 backdrop-blur p-8 sm:p-10 my-16">
        <div className="flex flex-col items-center text-center">
          <LogoMark className="w-14 h-14 text-gold" />
          <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">
            {mode === "login" ? "Member Login" : "Create Account"}
          </h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-2">
            Members HQ — Authorized Only
          </p>
        </div>

        <div className="grid grid-cols-2 mt-8 border border-gold/25">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`${m}-tab`}
              onClick={() => { setMode(m); setError(""); }}
              className={`py-2.5 font-condensed uppercase tracking-[0.2em] text-sm transition-colors duration-300 ${
                mode === m ? "bg-gold text-obsidian font-semibold" : "text-stone-400 hover:text-gold"
              }`}
            >
              {m === "login" ? "Sign In" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-7 space-y-5" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Full Name</Label>
              <Input data-testid="auth-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
          )}
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Email</Label>
            <Input data-testid="auth-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </div>
          <div>
            <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Password</Label>
            <Input data-testid="auth-password-input" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
          </div>

          {error && (
            <p className="border border-crimson/50 bg-crimson/10 text-crimson text-sm px-4 py-3" data-testid="auth-error">{error}</p>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={busy}
            className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold py-3.5 hover:bg-gold-light hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-[background-color,box-shadow] duration-300 disabled:opacity-60"
          >
            {busy ? "One moment..." : mode === "login" ? "Enter the HQ" : "Join & Enter"}
          </button>
        </form>

        <div className="mt-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-gold/15" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">or</span>
          <div className="h-px flex-1 bg-gold/15" />
        </div>

        <button
          type="button"
          data-testid="google-signin-button"
          onClick={googleLogin}
          className="mt-5 w-full flex items-center justify-center gap-3 border border-gold/30 bg-obsidian/60 text-parchment font-condensed uppercase tracking-[0.2em] text-sm py-3.5 hover:border-gold/60 hover:bg-card2 transition-[background-color,border-color] duration-300"
        >
          <GoogleG className="w-4 h-4" />
          Continue with Google
        </button>

        <p className="mt-6 text-center text-xs text-stone-500">
          Not a member yet? <Link to="/join" data-testid="login-join-link" className="text-gold underline underline-offset-4">Apply to join the order</Link>
        </p>
      </div>
    </main>
  );
}
